/*  CreateStageCardForm.tsx — aligned with Slimmed StageCardFormValues  */
import React, { useState } from "react";
import { Paper, useTheme } from "@mui/material";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import CustomStepper from "./CustomStepper";
import { StageCardFormValues } from "@/types/Card";
/* step screens — update paths to your real files */
import StepOne_StageInfo from "./StepOne_StageInfo";
import StepTwo_Pricing from "./StepTwo_Pricing";
import StepThree_StageMedia from "./StepThree_StageMedia";
import StepFour_StageReviewAndPublish from "./StepFour_StageInfo";
import { useCurrentTalentProfile } from "../../../hooks";

/* ——— step meta used by the stepper ——— */
const steps = [
  { label: "Basic Info", key: "basic" },
  { label: "Media & Branding", key: "media" },
  { label: "Session & Pricing", key: "pricing" },
  { label: "Review & Publish", key: "review" },
];

export default function CreateStageCardForm({
  onSuccess,
  mode = "create",
  initialValues,
}: {
  onSuccess: (data: any) => void;
  mode?: "create" | "edit";
  initialValues?: StageCardFormValues;
}) {
  const theme = useTheme();
  const navigate = useNavigate();

  /* ——— local step state ——— */
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const { currentProfile } = useCurrentTalentProfile();
  const showSnackbarMessage = (message: string) => {
    setSnackbarMessage(message);
    setShowSnackbar(true);
  };

  /* ---------- FORM ---------- */
  const formik = useFormik<StageCardFormValues>({
    initialValues: {
      /* Basic */
      talentId: initialValues?.talentId || 0, // or fetch from auth context
      title: initialValues?.title || "",
      venue_size: initialValues?.venue_size || "Small",
      banner_title: initialValues?.banner_title || "",

      /* Media */
      primaryThumbnail: initialValues?.primaryThumbnail || null,
      previewVideoUrl: initialValues?.previewVideoUrl || "",
      galleryImages: initialValues?.galleryImages || [],

      /* Pricing / session */
      price: initialValues?.price || 0,
      visibility: initialValues?.visibility || "public",

      /* Required by type but not captured yet */
      duration: initialValues?.duration || 0,
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title required"),
      venue_size: Yup.mixed<StageCardFormValues["venue_size"]>()
        .oneOf(["Small", "Medium", "Large", "Broadcast"])
        .required(),
      price: Yup.number().positive().required(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        console.log(values, "error values", formik.errors);

        const uploadFile = async (file: File): Promise<string> => {
          console.log("📦 Uploading file:", file.name);

          const formData = new FormData();
          formData.append("file", file);

          const uploadRes = await fetch(
            "https://api.taasnet.com/api/v1/file-upload",
            {
              method: "POST",
              body: formData,
              headers: {
                Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
                Accept: "*/*",
              },
            }
          );
          if (!uploadRes.ok) {
            const errText = await uploadRes.text();
            throw new Error(`❌ Upload failed: ${errText}`);
          }
          const uploadJson = await uploadRes.json();
          if (!uploadJson?.data?.url) {
            throw new Error(
              "❌ Unexpected response structure: 'data.url' not found"
            );
          }
          const uploadedUrl = uploadJson.data.url;
          console.log("✅ Uploaded image URL:", uploadedUrl);
          return uploadJson.data.url;
        };

        // Handle thumbnail
        let primary_thumbnail = "";

        if (values.primaryThumbnail instanceof File) {
          try {
            const uploaded = await uploadFile(values.primaryThumbnail);
            if (uploaded) {
              primary_thumbnail = uploaded;
            }
          } catch (err) {
            console.warn(
              "⚠️ Failed to upload thumbnail, using fallback image."
            );
            const fallbackId = Math.floor(Math.random() * 1000);
            primary_thumbnail = `https://picsum.photos/seed/${fallbackId}/600/400`;
          }
        } else if (
          typeof values.primaryThumbnail === "string" &&
          values.primaryThumbnail.trim() !== ""
        ) {
          primary_thumbnail = values.primaryThumbnail;
        } else {
          const fallbackId = Math.floor(Math.random() * 1000);
          primary_thumbnail = `https://picsum.photos/seed/${fallbackId}/600/400`;
        }

        // Handle gallery images
        const galleryUrls: string[] = [];
        if (Array.isArray(values.galleryImages)) {
          for (const img of values.galleryImages as (File | string)[]) {
            if (typeof img === "string") {
              galleryUrls.push(img);
            } else if (img instanceof File) {
              try {
                const uploaded = await uploadFile(img);
                galleryUrls.push(uploaded);
              } catch (err) {
                console.warn("⚠️ Error uploading gallery image:", err);
              }
            }
          }
        }

        const sessionCode = Math.floor(
          100000 + Math.random() * 900000
        ).toString();

        const payload = {
          talent_id: currentProfile?.id,
          title: values.title,
          venue_size: values.venue_size,
          price: Number(values.price),
          duration: Number(values.duration),
          primary_thumbnail: primary_thumbnail,
          preview_video_url: values.previewVideoUrl || "",
          status: values?.status || "Draft",
          is_public: values?.visibility === "public",
          session_code: sessionCode,
          gallery_images: galleryUrls,
          banner_title: values?.banner_title || "",
          description: values?.description || "",
          short_description: values?.short_description || "",
        };

        console.log("🚀 Final Payload:", JSON.stringify(payload, null, 2));

        const endpoint =
          mode === "edit"
            ? `https://api.taasnet.com/api/v1/stagecards/${values.id}`
            : "https://api.taasnet.com/api/v1/stagecards";

        const response = await fetch(endpoint, {
          method: mode === "edit" ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const contentType = response.headers.get("content-type");
          let errorMessage = `❌ Error ${response.status}: ${response.statusText}`;

          if (contentType?.includes("application/json")) {
            const errorJson = await response.json();
            if (errorJson.message) errorMessage += `\n🧾 ${errorJson.message}`;
            if (errorJson.error) errorMessage += `\n⚠️ ${errorJson.error}`;
            if (typeof errorJson.errors === "object") {
              const details = Object.entries(errorJson.errors)
                .map(([field, msg]) => `• ${field}: ${msg}`)
                .join("\n");
              errorMessage += `\n${details}`;
            }
          } else {
            const rawText = await response.text();
            errorMessage += `\n${rawText}`;
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();
        onSuccess(result?.data);
        formik.resetForm();
      } catch (error: any) {
        const errMsg =
          error?.message || "Something went wrong. Please try again.";
        console.error("🛑 Submission Error:", errMsg);
        showSnackbarMessage(errMsg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  /* ——— simple per-step validation ——— */
  const stepFields: Record<number, (keyof StageCardFormValues)[]> = {
    0: ["title", "venue_size"],
    2: ["price"],
  };

  const validateCurrentStep = async (stepIndex: number) => {
    await formik.validateForm();
    const errs = formik.errors;

    // cast errs to an indexable map of booleans
    return !stepFields[stepIndex]?.some((key) =>
      Boolean((errs as Record<keyof StageCardFormValues, unknown>)[key])
    );
  };

  /* ——— nav handlers ——— */
  const handleNext = async () => {
    if (!(await validateCurrentStep(activeStep))) return;
    setCompletedSteps((prev) => [...new Set([...prev, activeStep])]);
    setActiveStep((s) => s + 1);
  };

  const handleStepClick = (i: number) => {
    if (completedSteps.includes(i)) setActiveStep(i);
  };

  const progress = Math.floor(
    (completedSteps.length / (steps.length - 1)) * 100
  );

  return (
    <FormikProvider value={formik}>
      <Paper elevation={0} sx={{ px: 4, maxWidth: 960, mx: "auto" }}>
        <CustomStepper
          activeStep={activeStep}
          onStepClick={handleStepClick}
          completedSteps={completedSteps}
          progress={progress}
          onClickHeader={() => navigate(-1)}
          mode="create"
        />

        {activeStep === 0 && (
          <StepOne_StageInfo formik={formik} onNext={handleNext} />
        )}
        {activeStep === 1 && (
          <StepTwo_Pricing
            formik={formik}
            currentProfile={formik.values.talentId}
            onNext={handleNext}
            onBack={() => setActiveStep(0)}
          />
        )}
        {activeStep === 2 && (
          <StepThree_StageMedia
            formik={formik}
            currentProfile={formik.values.talentId}
            onNext={handleNext}
            onBack={() => setActiveStep(1)}
          />
        )}
        {activeStep === 3 && (
          <StepFour_StageReviewAndPublish
            onBack={() => setActiveStep(2)}
            onSubmit={formik.submitForm}
          />
        )}
      </Paper>
    </FormikProvider>
  );
}
